import { FocusMonitor } from "@angular/cdk/a11y";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { CdkStepper } from "@angular/cdk/stepper";
import {
	booleanAttribute,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewContainerRef,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatLegacyDialog } from "@angular/material/legacy-dialog";
import { MatLegacyMenuTrigger as MatMenuTrigger } from "@angular/material/legacy-menu";
import { Router } from "@angular/router";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import { isProjectUser } from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import { createClientProjectSearch } from "@app/_helpers/search";
import {
	canBookProjectTaskCombo,
	createRxValue,
	distinctUntilChangedJson,
	toBookingUserSettings,
} from "@app/_helpers/utils";
import { DefaultEntityTranslatePipe } from "@app/_pipes/default-entity-translate.pipe";
import { AppService } from "@app/app.service";
import { clientProjectDialogId } from "@app/shared/dialogs/client-project-picker-stepper-dialog/client-project-picker-stepper-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import {
	Project,
	ProjectsQuery,
	ProjectsService,
	Task,
	TasksQuery,
	TasksService,
	UserService,
	UserSettingsQuery,
} from "@timeghost/web-api";
import produce from "immer";
import { BehaviorSubject, combineLatest, defer } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap } from "rxjs/operators";
import { firstBy } from "thenby";

type SelectedEntity = {
	project: Project;
	task?: Task;
};
@Component({
	selector: "tg-project-list",
	templateUrl: "./project-list.component.html",
	styleUrls: ["./project-list.component.scss"],
})
export class ProjectListComponent implements OnInit {
	search = new FormControl("");
	searchActive = false;
	private searchTimeout: any;
	@ViewChild("searchElement", { static: true }) private searchElement: HTMLInputElement;
	focusSearch() {
		this.focusMonitor.focusVia(this.searchElement, this.viewElement.element.nativeElement);
	}
	onItemMouseEnter() {
		this.searchActive = false;
	}
	onItemMouseLeave() {
		this.searchActive = false;
	}
	private _selectedEntity = createRxValue<SelectedEntity>();
	public get selectedEntity(): SelectedEntity {
		return this._selectedEntity.value;
	}
	readonly showNextItemIcon = createRxValue<boolean>(false);
	@Input({ transform: booleanAttribute, alias: "showNextItemIcon" })
	public set showNextItemIconInput(value: boolean) {
		this.showNextItemIcon.next(value);
	}
	@Input()
	public set selectedEntity(v: SelectedEntity) {
		this._selectedEntity.next(v);
	}
	private _userId = createRxValue<string | undefined>(undefined);
	@Input()
	set userId(val: string | undefined) {
		this._userId.next(val);
	}
	get userId() {
		return this._userId.value;
	}
	@Output("entityChange") selectedEntityChange = new EventEmitter<SelectedEntity>();
	readonly entries$ = this.projectsQuery.selectAll({
		filterBy: (x) => !x?.completed && x.projectType !== "template",
	});
	readonly entries$filtered = combineLatest([
		this.entries$,
		this.search.valueChanges.pipe(startWith(""), debounceTime(300), distinctUntilChanged()),
		this.userSettingsQuery.select((x) => x.pinnedProjects).pipe(debounceTime(50)),
		this._selectedEntity.asObservable().pipe(startWith(this.selectedEntity), distinctUntilChanged()),
		this._userId.asObservable().pipe(startWith(this.userId), distinctUntilChanged()),
		this.userSettingsQuery.select(),
	]).pipe(
		map(
			([entries, q, pinnedProjects, selected, userId, me]: [
				Project[],
				string,
				string[],
				SelectedEntity,
				string | undefined,
				ReturnType<UserSettingsQuery["getValue"]>,
			]) => {
				const booker = toBookingUserSettings(me, userId);
				if (!q?.length)
					return [entries, pinnedProjects, selected, booker] as [
						Project[],
						string[],
						SelectedEntity,
						ReturnType<typeof toBookingUserSettings>,
					];

				return [
					createClientProjectSearch(entries, {
						translateKeys: {
							client: (entity) => this.translateName.transform(entity, "client", "client.none"),
							project: (entity) => this.translateName.transform(entity, "project", "project.none"),
						},
					})
						.search(q)
						.map((x) => x.project),
					pinnedProjects,
					selected,
					booker,
				] as [Project[], string[], SelectedEntity, ReturnType<typeof toBookingUserSettings>];
			}
		),
		map(
			([entries, pinnedProjects, selected, booker]: [
				Project[],
				string[],
				SelectedEntity,
				ReturnType<typeof toBookingUserSettings>,
			]) => {
				const sorted = entries
					.map((x) => ({
						...x,
						pinned: pinnedProjects.findIndex((p) => p === x.id) !== -1,
						selected: selected?.project && x.id === selected.project.id,
					}))
					.filter((p) => isProjectUser(booker.id, booker.workspace, p) || !p.private)
					.sort(
						// @ts-ignore
						firstBy((x) => x.useAsDefault)
							// @ts-ignore
							.thenBy((x) => x.selected, "desc")
							// @ts-ignore
							.thenBy((a, b) => b.pinned - a.pinned)
							.thenBy((a: any, b: any) =>
								a.pinned && b.pinned
									? pinnedProjects.findIndex((x) => x === a.id) -
										pinnedProjects.findIndex((x) => x === b.id)
									: 0
							)
							.thenBy((x) => x.name, "asc")
					);
				return sorted.map((entry, i, arr) => ({
					...entry,
					showUnpinnedDivider: i > 0 && !entry.pinned && !!arr[i - 1]?.pinned,
					firstPinned: !!!pinnedProjects.findIndex((x) => x == entry.id),
				}));
			}
		)
	);
	private _taskName = new BehaviorSubject<string>(null);
	readonly taskName$ = this._taskName.asObservable().pipe(distinctUntilChanged());
	get taskName() {
		return this._taskName.getValue();
	}
	@Input("suggestProjectByName")
	set taskName(val: string) {
		this._taskName.next(val);
	}
	@ViewChild(CdkVirtualScrollViewport, { static: true }) viewport: CdkVirtualScrollViewport;
	readonly showMore = createRxValue(false);
	readonly entries$suggestions = combineLatest([
		this.taskName$.pipe(startWith(this.taskName), distinctUntilChanged(), debounceTimeAfterFirst(100)),
		this.selectedEntityChange.asObservable().pipe(startWith(this.selectedEntity)),
		this.showMore.value$,
		this._userId.asObservable().pipe(startWith(this.userId), distinctUntilChanged()),
		this.userSettingsQuery.select(),
		this.projectsQuery.selectAll().pipe(
			map((projects) => projects.map((p) => p.id).join("|")),
			distinctUntilChanged()
		),
	]).pipe(
		filter(([taskName]) => taskName?.length >= 2),
		distinctUntilChangedJson(),
		switchMap(([taskName, selected, showMore, userId, me]) =>
			defer(() => {
				const booker = toBookingUserSettings(me, userId);
				return this.projectsService
					.findByTaskName(taskName)
					.then((entries) => entries?.uniqBy(({ project, task }) => `${project?.id}${task?.id}`))
					.then(async (entries) => {
						const taskIds = [
							...new Set((entries ?? []).map((item) => item?.task?.id).filter(Boolean) as string[]),
						];
						const resolvedTasks = await Promise.all(
							taskIds.map(async (id) => [id, await this.resolveTaskForBooking(id)] as const)
						);
						const taskById = new Map(resolvedTasks);
						return (entries ?? [])
							.filter((item) => {
								if (!item?.project?.id) return false;
								const project = this.projectsQuery.getEntity(item.project.id);
								const task = item.task?.id ? taskById.get(item.task.id) : null;
								if (item.task?.id && !task) return false;
								return canBookProjectTaskCombo(project, task ?? undefined, booker);
							})
							.map(({ project, task }) => {
								const stored = this.projectsQuery.getEntity(project.id);
								const resolvedTask = task?.id ? taskById.get(task.id) : null;
								return {
									...stored,
									...project,
									task: resolvedTask ?? task,
									selected:
										selected &&
										selected.project?.id === project.id &&
										selected.task?.id === task?.id,
								};
							})
							.filter((d: any) => !d.completed);
					});
			}).pipe(
				map((x) => {
					return x.sort(firstBy("selected", "desc").thenBy("name"));
				}),
				map((x) => {
					const maxItems = 3;
					return {
						entries: x?.length > 0 ? (showMore ? x : x.slice(0, maxItems)) : null,
						length: x?.length,
						showMore: x?.length > maxItems ? showMore : undefined,
					};
				})
			)
		),
		map((x) => (x.entries?.length > 0 ? x : null))
	);
	constructor(
		private projectsQuery: ProjectsQuery,
		private userSettingsQuery: UserSettingsQuery,
		private userService: UserService,
		private projectsService: ProjectsService,
		private tasksService: TasksService,
		private tasksQuery: TasksQuery,
		private stepper: CdkStepper,
		private cdr: ChangeDetectorRef,
		private router: Router,
		private translateName: DefaultEntityTranslatePipe,
		private focusMonitor: FocusMonitor,
		private viewElement: ViewContainerRef,
		private dialogs: MatLegacyDialog,
		private appService: AppService,
		private translate: TranslateService
	) {}
	private async resolveTaskForBooking(taskId: string): Promise<Task | null> {
		const cached = this.tasksQuery.getEntity(taskId);
		if (cached) return cached;
		try {
			return (await toPromise(this.tasksService.getById(taskId))) ?? null;
		} catch {
			return null;
		}
	}
	ngOnInit(): void {
		this.search.valueChanges.subscribe(() => {
			this.searchActive = true;

			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}

			this.searchTimeout = setTimeout(() => {
				this.searchActive = false;
			}, 100);
		});
	}
	trackId(i: number, { id }: { id: string }) {
		return id;
	}
	submitProject(entity?: SelectedEntity) {
		if (entity?.project && entity.project.billable === undefined)
			entity.project = this.projectsQuery.getEntity(entity.project.id) || entity.project;
		const booker = toBookingUserSettings(this.userSettingsQuery.getValue(), this.userId);
		const project = entity?.project?.id
			? (this.projectsQuery.getEntity(entity.project.id) ?? entity.project)
			: null;
		if (!canBookProjectTaskCombo(project, entity?.task, booker)) {
			this.appService.notifyInfo("time.project-task-not-available");
			return;
		}
		this.selectedEntity = entity;
		const totalTime = entity.project.timesRecorded
			? (entity.project.timesRecorded.billable?.clamp(0, entity.project.timesRecorded.billable) || 0) +
				(!entity.project.billable
					? entity.project.timesRecorded.notBillable?.clamp(0, entity.project.timesRecorded.notBillable) || 0
					: 0)
			: 0;
		if (entity.project.estimation && totalTime >= entity.project.estimation) {
			this.appService.notifyInfo("project.budget.limitReached");
		}
		this.selectedEntityChange.emit(entity);
	}
	togglePinProject(id: string) {
		return (
			this.isProjectPinned(id) ? this.userService.removePinnedProject(id) : this.userService.addPinnedProject(id)
		).toPromise();
	}
	isProjectPinned(id: string) {
		return this.userSettingsQuery.getValue().pinnedProjects?.findIndex((x) => x === id) >= 0;
	}
	movePinnedProject(id: string) {
		const pinnedProjects: string[] = this.userSettingsQuery.getValue().pinnedProjects;
		const previousIndex = pinnedProjects.findIndex((x) => x === id);
		if (!previousIndex) return;
		this.userService
			.updatePinnedProjects(
				produce(this.userSettingsQuery.getValue(), (draft) => {
					moveItemInArray(draft.pinnedProjects, previousIndex, 0);
				}).pinnedProjects
			)
			.subscribe();
	}
	projectMenuPosition = { x: "0px", y: "0px" };
	openContextMenu(event: MouseEvent, trigger: MatMenuTrigger, data: any) {
		event.stopPropagation(), event.preventDefault();
		this.projectMenuPosition.x = event.clientX + "px";
		this.projectMenuPosition.y = event.clientY + "px";
		trigger.menuData = data;
		trigger.menu.focusFirstItem("mouse");
		trigger.openMenu();
	}
	openProject(id: string) {
		return this.router
			.navigate(["/settings/projects/" + id + "/report"])
			.then(() => this.dialogs.getDialogById(clientProjectDialogId)?.close());
	}
}
